/**
 * SyncEngine — orchestrates mutation queue flushing.
 *
 * Dequeues pending mutations, calls app-defined handlers,
 * handles partial success, and manages retry logic.
 */

import type { MutationQueue } from '../queue/MutationQueue';
import type { PhotoStore } from '../photos/PhotoStore';
import type { QueuedMutation, SyncHandler, SyncHandlerResult, SyncResult } from '../queue/types';

export interface SyncEngineConfig {
  readonly queue: MutationQueue;
  readonly photoStore: PhotoStore;
  readonly handlers: Record<string, SyncHandler>;
  readonly onConflict?: (mutation: QueuedMutation, error: string) => void;
  readonly onSyncComplete?: (results: readonly SyncResult[]) => void;
  /**
   * Longest a single send may run before the engine gives up on it, releases `syncing` and retries the row on the
   * next trigger (default DEFAULT_HANDLER_TIMEOUT_MS). Without it a send that never settles (stalled upload, blocked
   * IndexedDB) leaves `syncing` set and every later trigger returns at once until the app is reloaded: a tech's
   * queued report then waits for the next morning (measured 2026-11-01: 2 of 4 production offline Submits took 15-19 h).
   */
  readonly handlerTimeoutMs?: number;
  /**
   * Mutation types whose handler is safe to run twice at once for the same row (same server result either way, e.g.
   * a transactional create-or-merge keyed on a client-minted id). Only these may be re-sent while an earlier send of
   * the same row is still unsettled. EVERY OTHER TYPE IS NEVER RE-SENT while its earlier send is unsettled: the row
   * waits, and the earlier send's late result is applied when it arrives (a second send could add a photo twice or
   * lay older values over newer ones). Default: none.
   */
  readonly idempotentTypes?: readonly string[];
}

export class SendTimeoutError extends Error {
  constructor(readonly afterMs: number) {
    super(`Send timed out after ${Math.round(afterMs / 1000)}s`);
    this.name = 'SendTimeoutError';
  }
}

/** Above Firebase Storage's own 10-minute upload retry ceiling, so a slow-but-live upload is never cut off. */
export const DEFAULT_HANDLER_TIMEOUT_MS = 12 * 60 * 1000;

export interface SyncStatus {
  readonly pendingCount: number;
  readonly failedCount: number;
  readonly isSyncing: boolean;
  readonly lastSyncAt: number | null;
  readonly lastError: string | null;
}

type StatusListener = (status: SyncStatus) => void;

/**
 * Thrown by discardFailed() when it refuses to act. Two refusal reasons:
 * the mutation is not (or no longer) in the 'failed' state, or it opted into
 * retainBlobs (its blobs are owner-scoped and evicted by the app via
 * PhotoStore.deleteBlobsByOwner at its own terminal point — e.g. forms
 * submit/save — not by this generic path). discardFailed does NOT resolve
 * silently in either case, unlike a mutation that has simply already been
 * removed (a harmless race with another tab/retry, treated as a no-op).
 */
export class DiscardRefusedError extends Error {
  constructor(readonly mutationId: string, readonly reason: string) {
    super(`Cannot discard mutation ${mutationId}: ${reason}`);
    this.name = 'DiscardRefusedError';
  }
}

/**
 * Thrown by discardFailed() when a sync (or another exclusive section) is
 * already holding runExclusive's lock. This is DELIBERATELY a distinct type
 * from DiscardRefusedError: a refusal means "this mutation can never be
 * discarded this way", while busy means "try again in a moment" — collapsing
 * both into a silent no-op (runExclusive's own contract) left a caller unable
 * to tell "nothing happened because it's already gone" apart from "nothing
 * happened because a sync is running right now". Never thrown from inside
 * runExclusive's `fn` — checked up front, before the lock is even attempted.
 */
export class DiscardBusyError extends Error {
  constructor(readonly mutationId: string) {
    super(`Cannot discard mutation ${mutationId}: a sync is already in progress`);
    this.name = 'DiscardBusyError';
  }
}

/**
 * A caller (e.g. SurveyCapture's photo annotation flow, #2837) may record a
 * pristine "original" blob paired with an annotated one in its payload under
 * `originalBlobKeys` (annotated blobKey -> original blobKey). The original is
 * not a photo the rep thinks of as separate — it exists purely so the
 * un-annotated version is recoverable server-side. Soft, best-effort
 * convention: absent on any payload that doesn't use it.
 */
function extractOriginalBlobKeys(payload: unknown): ReadonlySet<string> {
  if (!payload || typeof payload !== 'object') return new Set();
  const map = (payload as Record<string, unknown>).originalBlobKeys;
  if (!map || typeof map !== 'object') return new Set();
  return new Set(Object.values(map as Record<string, unknown>).filter((v): v is string => typeof v === 'string'));
}

export class SyncEngine {
  private readonly config: SyncEngineConfig;
  private syncing = false;
  /** Set when a watchdog fires during the current sync() run; read by the loop to stop after the first stall. */
  private timedOutThisRun = false;
  /** Handler calls not yet settled, per mutation id (abandoned-by-watchdog sends stay counted until they settle). */
  private readonly unsettled = new Map<string, number>();
  /** Held by runExclusive: sync() will not start while true. Deliberately NOT
   *  reported as isSyncing — nothing is being sent, so the UI must not say so. */
  private exclusive = false;
  private lastSyncAt: number | null = null;
  private lastError: string | null = null;
  private readonly listeners = new Set<StatusListener>();

  constructor(config: SyncEngineConfig) {
    this.config = config;
  }

  /** Subscribe to status changes. Returns unsubscribe function. */
  onStatusChange(callback: StatusListener): () => void {
    this.listeners.add(callback);
    this.notifyListeners();
    return () => { this.listeners.delete(callback); };
  }

  /** Get current sync status. */
  async getStatus(): Promise<SyncStatus> {
    const [pendingCount, failedCount] = await Promise.all([
      this.config.queue.count('pending'),
      this.config.queue.count('failed'),
    ]);
    return {
      pendingCount,
      failedCount,
      isSyncing: this.syncing,
      lastSyncAt: this.lastSyncAt,
      lastError: this.lastError,
    };
  }

  /**
   * Requeue failed mutations and sync. This is what a "retry" tap should
   * call — plain sync() only processes 'pending' items and never touches
   * 'failed' ones (2026-07 survey audit, finding A4).
   */
  async retryFailed(): Promise<readonly SyncResult[]> {
    await this.config.queue.requeueFailed();
    this.notifyListeners();
    return this.sync();
  }

  /**
   * List every DISCARDABLE failed mutation with how many photos it is really
   * holding, so a UI can show the rep what a Discard would delete before
   * they confirm it. Two accuracy requirements, both load-bearing:
   *
   * 1. A retainBlobs mutation is excluded entirely — discardFailed() refuses
   *    those (see its own doc comment), so listing one here with a Discard
   *    action would be a button that always errors.
   * 2. photoCount counts only blobs that ACTUALLY EXIST in the photo store
   *    right now (not just blobKeys.length, which can include keys whose
   *    blobs were already evicted some other way) and excludes "annotation
   *    original" blobs (see extractOriginalBlobKeys) — those aren't a photo
   *    the rep thinks of as separate, so counting them would overstate what
   *    Discard removes and make the confirmation dialog lie.
   */
  async getFailedMutations(): Promise<ReadonlyArray<{ readonly mutation: QueuedMutation; readonly photoCount: number }>> {
    const failed = await this.config.queue.getByStatus('failed');
    const discardable = failed.filter((mutation) => !mutation.retainBlobs);
    return Promise.all(
      discardable.map(async (mutation) => {
        const originals = extractOriginalBlobKeys(mutation.payload);
        let photoCount = 0;
        for (const key of mutation.blobKeys) {
          if (originals.has(key)) continue;
          const stored = await this.config.photoStore.getBlob(key);
          if (stored) photoCount += 1;
        }
        return { mutation, photoCount };
      }),
    );
  }

  /**
   * Permanently discard a failed mutation: delete its blobs, then the queue
   * row. This is the ONLY path that deletes a failed mutation's blobs —
   * a failure alone (sync(), retryFailed(), or exhausting retries) must
   * never free them, or a rep could lose a visit's photos with no action of
   * their own. Callers MUST gate this behind an explicit, confirmed user
   * action naming what will be lost.
   *
   * Runs inside runExclusive and RE-READS the mutation there, so it cannot
   * race a concurrent sync() that is mid-flight on the same row (mirrors the
   * age-cap sweep pattern runExclusive itself documents). If another sync or
   * exclusive section is already running, this call simply does nothing —
   * exactly like runExclusive's own contract — so a caller that cares should
   * retry.
   *
   * Refuses (throws DiscardRefusedError) rather than silently doing nothing
   * when:
   *   - the mutation is no longer 'failed' (e.g. a concurrent retry moved it
   *     to 'pending'/'syncing'/gone-because-it-just-succeeded) — discarding it
   *     now would delete blobs a send might still be using.
   *   - the mutation has retainBlobs set — those blobs are owner-scoped and
   *     evicted by the app itself via PhotoStore.deleteBlobsByOwner at its
   *     own terminal point (forms save/submit); there is no generic "owner"
   *     concept here for this path to evict by, so it refuses rather than
   *     guessing.
   * Resolves silently (no-op, no throw) only when the mutation is already
   * gone entirely — a harmless race with another tab/retry finishing first.
   *
   * Blobs are deleted BEFORE the queue row (mirrors processMutation's own
   * cleanup order) so a failure mid-discard leaves the mutation still
   * visible/retryable rather than silently orphaning its blobs.
   *
   * Deletes by the mutation's own blobKeys, not deleteBlobsByMutation(id) —
   * see the identical comment in processMutation's success cleanup for why
   * that index lookup can silently match nothing.
   */
  async discardFailed(mutationId: string): Promise<void> {
    // Checked synchronously, before attempting the lock (no await in
    // between) — this cannot race runExclusive's own identical check.
    if (this.syncing || this.exclusive) {
      throw new DiscardBusyError(mutationId);
    }
    await this.runExclusive(async () => {
      const mutation = await this.config.queue.get(mutationId);
      if (!mutation) return; // already gone — harmless race, no-op
      if (mutation.retainBlobs) {
        throw new DiscardRefusedError(
          mutationId,
          'retainBlobs mutations are evicted by owner (deleteBlobsByOwner), not discarded',
        );
      }
      if (mutation.status !== 'failed') {
        throw new DiscardRefusedError(mutationId, `mutation status is '${mutation.status}', not 'failed'`);
      }
      await Promise.all(mutation.blobKeys.map((key) => this.config.photoStore.deleteBlob(key)));
      await this.config.queue.delete(mutationId);
    });
  }

  /**
   * Run `fn` while no sync can start, so it can read-then-delete queue rows without
   * racing a send (e.g. the forms outbox's age-cap sweep must never delete a report
   * that is on its way). Returns undefined WITHOUT running `fn` if a sync or another
   * exclusive section is already in progress; the caller simply tries again later.
   * Scope: this engine instance (one tab). It does not lock other tabs.
   */
  async runExclusive<T>(fn: () => Promise<T>): Promise<T | undefined> {
    if (this.syncing || this.exclusive) return undefined;
    this.exclusive = true;
    try {
      return await fn();
    } finally {
      this.exclusive = false;
      // The section may have changed the queue (e.g. deleted expired rows); let
      // screens that show the queue re-read it.
      this.notifyListeners();
    }
  }

  /** Trigger a sync. Safe to call multiple times — guards against concurrent runs. */
  async sync(): Promise<readonly SyncResult[]> {
    if (this.syncing || this.exclusive) return [];
    if (!navigator.onLine) return [];

    this.syncing = true;
    this.timedOutThisRun = false;
    this.lastError = null;
    this.notifyListeners();

    const results: SyncResult[] = [];

    try {
      const pending = await this.config.queue.getByStatus('pending');

      for (const mutation of pending) {
        if (!navigator.onLine) break;

        const result = await this.processMutation(mutation);
        results.push(result);
        this.notifyListeners();
        // A stalled send usually means the network or storage is stuck, not this one row: stop here so `syncing`
        // is released and the next trigger starts clean, instead of stalling again on every remaining row.
        if (this.timedOutThisRun) break;
      }

      this.lastSyncAt = Date.now();
      this.config.onSyncComplete?.(results);
    } catch (err: unknown) {
      this.lastError = err instanceof Error ? err.message : 'Sync failed';
    } finally {
      this.syncing = false;
      this.notifyListeners();
    }

    return results;
  }

  /**
   * Rejects if the work has not settled in time and releases the caller. The abandoned work keeps running and stays
   * counted in `unsettled`; when it settles, its result is applied (see sendOne), never dropped.
   */
  private withWatchdog<T>(work: () => Promise<T>): Promise<T> {
    const ms = this.config.handlerTimeoutMs ?? DEFAULT_HANDLER_TIMEOUT_MS;
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.timedOutThisRun = true;
        reject(new SendTimeoutError(ms));
      }, ms);
      work().then(
        (v) => { clearTimeout(timer); resolve(v); },
        (e: unknown) => { clearTimeout(timer); reject(e instanceof Error ? e : new Error(String(e))); },
      );
    });
  }

  private isIdempotent(type: string): boolean {
    return this.config.idempotentTypes?.includes(type) === true;
  }

  /** Mark the row syncing, gather its blobs, run the handler. Counted in `unsettled` until `release` (after its result is applied). */
  private startSend(mutation: QueuedMutation, handler: SyncHandler): Promise<SyncHandlerResult> {
    this.unsettled.set(mutation.id, (this.unsettled.get(mutation.id) ?? 0) + 1);
    const send = (async () => {
      await this.config.queue.updateStatus(mutation.id, 'syncing');
      const blobMap = new Map<string, Blob>();
      for (const blobKey of mutation.blobKeys) {
        const stored = await this.config.photoStore.getBlob(blobKey);
        if (stored) {
          blobMap.set(blobKey, stored.blob);
        }
      }
      return handler(mutation, blobMap);
    })();
    return send;
  }

  /**
   * Drop one unsettled-send count. Called only AFTER the send's result has been applied to the row (or the send
   * failed): between the send landing and the row being removed the row still looks pending, and a sync() in that
   * window must not send it again.
   */
  private release(id: string): void {
    const left = (this.unsettled.get(id) ?? 1) - 1;
    if (left <= 0) this.unsettled.delete(id); else this.unsettled.set(id, left);
  }

  private async processMutation(mutation: QueuedMutation): Promise<SyncResult> {
    const base = { mutationId: mutation.id, type: mutation.type };
    const handler = Object.prototype.hasOwnProperty.call(this.config.handlers, mutation.type)
      ? this.config.handlers[mutation.type]
      : undefined;
    if (!handler) {
      await this.config.queue.updateStatus(mutation.id, 'failed', `No handler for type: ${mutation.type}`);
      return { ...base, ok: false, error: `No handler for type: ${mutation.type}` };
    }
    if (this.unsettled.has(mutation.id) && !this.isIdempotent(mutation.type)) {
      // Never a second send of a row whose first is still alive: it waits for that send's own result.
      return { ...base, ok: false, error: 'An earlier send of this item is still running; not sending it twice' };
    }

    const send = this.startSend(mutation, handler);
    let result: SyncHandlerResult;
    try {
      result = await this.withWatchdog(() => send);
    } catch (err: unknown) {
      if (err instanceof SendTimeoutError) {
        // The send is still alive: when it settles, its result decides the row (delivered -> removed). The registry
        // entry stays until that late apply has finished (or the send failed), so nothing re-sends the row meanwhile.
        void send
          .then((late) => this.applyOutcome(mutation, late))
          .catch(() => undefined)
          .finally(() => this.release(mutation.id));
        return this.recordThrown(mutation, err);
      }
      try {
        return await this.recordThrown(mutation, err);
      } finally {
        this.release(mutation.id);
      }
    }
    try {
      return await this.withWatchdog(() => this.applyOutcome(mutation, result));
    } catch (err: unknown) {
      // The row's own bookkeeping stalled or failed: release `syncing`; a row left 'syncing' is re-queued on reload.
      const msg = err instanceof Error ? err.message : 'Storage error';
      this.lastError = msg;
      return { ...base, ok: false, error: msg };
    } finally {
      this.release(mutation.id);
    }
  }

  /** Judge the row by the handler's result. Runs for the live path and for a late-settling abandoned send. */
  private async applyOutcome(mutation: QueuedMutation, result: SyncHandlerResult): Promise<SyncResult> {
    const base = { mutationId: mutation.id, type: mutation.type };
    if (result.ok) {
      // Clean up blobs (unless the mutation opts out — e.g. forms retain
      // owner-scoped blobs across save/submit and evict explicitly by owner)
      // and delete the mutation.
      //
      // Deletes by mutation.blobKeys (each blob's OWN id), never by
      // deleteBlobsByMutation(mutation.id) — several callers (SurveyCapture,
      // CameraRollImport) generate their own client-side id for addBlob()
      // BEFORE calling queue.enqueue(), which mints a DIFFERENT id for the
      // mutation itself. deleteBlobsByMutation(mutation.id) then matches zero
      // rows and every one of that mutation's blobs leaks forever — on every
      // SUCCESSFUL sync, not only a failed one. blobKeys is the one field
      // guaranteed to name exactly this mutation's blobs regardless of what
      // id scheme the caller used to tag them (OFFICEHUB-SALES-8: this made
      // the reported burst-import case worse than it looked, since ordinary
      // successful uploads were never returning their storage either).
      if (!mutation.retainBlobs) {
        await Promise.all(mutation.blobKeys.map((key) => this.config.photoStore.deleteBlob(key)));
      }
      await this.config.queue.delete(mutation.id);
      return { ...base, ok: true };
    }

    if (result.retryable && mutation.retryCount < mutation.maxRetries - 1) {
      await this.config.queue.updateStatus(mutation.id, 'pending', result.error);
      return { ...base, ok: false, error: result.error };
    }

    // Permanent failure or max retries
    await this.config.queue.updateStatus(mutation.id, 'failed', result.error);
    this.config.onConflict?.(mutation, result.error ?? 'Unknown error');
    return { ...base, ok: false, error: result.error };
  }

  /** The handler threw (or timed out): put the row back to waiting, or failed once its retries are spent. Bounded. */
  private async recordThrown(mutation: QueuedMutation, err: unknown): Promise<SyncResult> {
    const msg = err instanceof Error ? err.message : 'Handler threw';
    const next = mutation.retryCount < mutation.maxRetries - 1 ? 'pending' : 'failed';
    try {
      await this.withWatchdog(() => this.config.queue.updateStatus(mutation.id, next, msg));
    } catch (writeErr: unknown) {
      this.lastError = writeErr instanceof Error ? writeErr.message : 'Storage error';
    }
    return { mutationId: mutation.id, type: mutation.type, ok: false, error: msg };
  }

  private notifyListeners(): void {
    Promise.all([
      this.config.queue.count('pending'),
      this.config.queue.count('failed'),
    ]).then(([pendingCount, failedCount]) => {
      const status: SyncStatus = {
        pendingCount,
        failedCount,
        isSyncing: this.syncing,
        lastSyncAt: this.lastSyncAt,
        lastError: this.lastError,
      };
      this.listeners.forEach((fn) => fn(status));
    }).catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Storage error';
      this.lastError = msg;
      const status: SyncStatus = {
        pendingCount: 0,
        failedCount: 0,
        isSyncing: this.syncing,
        lastSyncAt: this.lastSyncAt,
        lastError: msg,
      };
      this.listeners.forEach((fn) => fn(status));
    });
  }
}
